import { useState, useEffect, useRef } from "react";
import { Pencil, Check, X } from "lucide-react"; // optional icons

// type InlineEditTextProps = {
//   value: string;
//   canEdit?: boolean;
//   onSave: (next: string) => Promise<void> | void;

//   // Optional: customize how the "view" mode looks
//   renderDisplay?: (value: string) => React.ReactNode;

//   // Styling hooks
//   className?: string;        // for the display text
//   inputClassName?: string;   // for the input
//   showIcon?: boolean;
// };

export function InlineEdit({
  value,
  canEdit = false,
  onSave,
  isEditing,
  renderDisplay,
  renderEditor,
  className = "text-base font-normal",
  inputClassName = "bg-transparent border-b border-gray-300 focus:outline-none focus:border-black px-0 py-0.5 min-w-[8rem]",
  showIcon = false,
}) {
  const [localIsEditing, setLocalIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [currentValue, setCurrentValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  // keep local draft in sync if parent updates value
  useEffect(() => {
    setCurrentValue(value);
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (!isEditing) {
      cancelEditing();
    }
  }, [isEditing]);

  useEffect(() => {
    if (localIsEditing && inputRef.current) {
      // focus is safe for both input & file input
      if (typeof inputRef.current.focus === "function") {
        inputRef.current.focus();
      }
      // only call select() if it exists (text inputs)
      if (typeof inputRef.current.select === "function") {
        inputRef.current.select();
      }
    }
  }, [localIsEditing]);

  const startEditing = () => {
    if (!canEdit) return;
    setDraft(value);
    setLocalIsEditing(true);
  };

  const cancelEditing = () => {
    setDraft(value);
    setLocalIsEditing(false);
  };

  const handleSave = async () => {
    let next = draft;

    // If it's a string, support trimming + equality check
    if (typeof next === "string") {
      next = next.trim();
      if (next === value) {
        setLocalIsEditing(false);
        return;
      }
    } else {
      // For non-strings (e.g. File), do a simple identity check
      if (next === value) {
        setLocalIsEditing(false);
        return;
      }
    }

    try {
      setIsSaving(true);
      await onSave(next);
      setCurrentValue(next);
      setLocalIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEditing();
    }
  };

  // VIEW MODE
  if (!localIsEditing) {
    return (
      <div className="flex items-center gap-2">
        <div className={className} onClick={canEdit ? startEditing : undefined}>
          {renderDisplay ? renderDisplay(currentValue) : currentValue || "Untitled"}
        </div>
        {canEdit && showIcon && (
          <button
            type="button"
            onClick={startEditing}
            className="inline-flex items-center rounded-full p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            aria-label="Edit"
          >
            <Pencil className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  // EDIT MODE
  return (
    <div className="flex items-center gap-2">
      {renderEditor ? (
        renderEditor({
          value: draft,
          onChange: setDraft,
          onKeyDown: handleKeyDown,
          ref: inputRef,
          disabled: isSaving,
        })
      ) : (
        // default: simple text input
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
          className={`${className} bg-transparent border-b border-gray-300 focus:outline-none focus:border-black px-0 py-0.5 min-w-[8rem]`}
        />
      )}

      <button
        type="button"
        onClick={handleSave}
        disabled={isSaving}
        className="inline-flex items-center rounded-full p-1 text-green-600 hover:bg-green-50 disabled:opacity-50"
      >
        <Check className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={cancelEditing}
        disabled={isSaving}
        className="inline-flex items-center rounded-full p-1 text-red-500 hover:bg-red-50 disabled:opacity-50"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
